<template>
  <div style="display: inline">
    <v-menu offset-y>
      <template v-if="column" v-slot:activator="{ on, attrs }">
        <template v-if="pickerOnly">
          <div
            class="picker-only"
            v-bind="attrs"
            v-on="on"
            :title="column.title" @click="mode = ''">
              <div class="picker-only-text">{{ column.title }}</div>
              <v-icon class="float-right"
                >mdi-menu-down</v-icon
              >
          </div>
        </template>
        <template v-else>
          <v-row
            dense
            class="columnPicker"
            v-bind="attrs"
            v-on="on"
            :title="column.title"
          >
            <v-col @click="mode = ''" :cols="9 - (allowRemove ? 1 : 0)">
              <div class="pickerContent">
                <v-icon left small>mdi-cube-outline</v-icon>{{ column.title }}
              </div>
            </v-col>
            <v-col cols="1">
              <v-icon @click="mode = ''" class="columnAdder" title="Change column"
                >mdi-chevron-down</v-icon
              ></v-col
            >
            <v-col cols="1">
              <v-icon
                class="columnAdder"
                @click.stop="showColumnSettings"
                title="Custom settings"
                >mdi-cog</v-icon
              ></v-col
            >
            <v-col cols="1" v-if="allowRemove">
              <v-icon
                class="columnAdder"
                title="Remove column"
                @click.stop="$emit('removeColumn', column)"
                >mdi-minus-thick</v-icon
              ></v-col
            >
            <v-col cols="1" v-if="allowAdd">
              <v-icon
                class="columnAdder"
                @click="mode = 'add'"
                title="Add measure"
                >mdi-plus-thick</v-icon
              ></v-col
            >
          </v-row>
        </template>
      </template>
      <template v-else v-slot:activator="{ on, attrs }">
        <v-row v-bind="attrs" v-on="on">
          <v-col cols="1">
            <v-icon
              class="columnAdder"
              @click="mode = 'add'"
              title="Add measure"
              >mdi-plus-thick</v-icon
            ></v-col
          ></v-row
        >
      </template>
      <v-list dense style="max-height: 700px" v-if="column || mode === 'add'">
        <template v-if="!pickerOnly">
          <v-list-item>
            <v-list-item-group>{{
              mode === "add" ? "Pick column to add:" : "Change existing column:"
            }}</v-list-item-group> </v-list-item
          ><v-divider></v-divider>
        </template>
        <v-list-item
          dense
          v-for="(co, coi) in columnsAvailable.filter((x) => !x.visible || x.id === column.id)"
          :key="'co' + coi"
          :style="
            'background-color: ' +
            (column && co.id === column.id ? '#daecf2;' : 'white;')
          "
          @click="
            $emit(mode === 'add' ? 'addColumn' : 'changeColumn', co, column)
          "
        >
          <v-list-item-title>{{ co.title }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <v-dialog v-model="settings.show" max-width="1200">
      <v-card v-if="column">
        <v-card-title class="text-h5">
          {{ column.title }} Settings</v-card-title
        >
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="4">
                <v-text-field
                  outlined
                  clearable
                  v-model="settings.columnTitle"
                  dense
                  hide-details
                  label="Column Title"
                ></v-text-field></v-col
              ><v-col>
                <v-select
                  hide-details
                  outlined
                  dense
                  v-model="settings.alignment"
                  clearable
                  label="Alignment"
                  :items="['left', 'center', 'right']"
                ></v-select></v-col
              ><v-col>
                <v-select
                  hide-details
                  outlined
                  dense
                  v-model="settings.displayType"
                  return-object
                  label="Measure Display"
                  :items="measureDisplayTypes"
                  item-text="title"
                  item-value="id"
                ></v-select
              ></v-col>
              <v-col v-if="allowDefaultSort">
                <v-select
                  hide-details
                  outlined
                  dense
                  v-model="settings.defaultSort"
                  label="Default Sort"
                  title="Rows load pre-sorted by this measure; only one column or measure can hold the default sort"
                  :items="[
                    { text: 'None', value: null },
                    { text: 'Ascending', value: 'asc' },
                    { text: 'Descending', value: 'desc' },
                  ]"
                  item-text="text"
                  item-value="value"
                ></v-select
              ></v-col>
              <v-col v-if="column.column === 'job_count'">
                <v-switch
                  label="Hide Check Box?"
                  v-model="settings.hideSelector"
                ></v-switch>
              </v-col>
              <v-col v-else-if="column.column_basis">
                <v-text-field
                  outlined
                  clearable
                  v-model="settings.single_comparator_text"
                  dense
                  hide-details
                  title="Override text to appear if only single Comparator value is present"
                  label="Text for single Comparator"
                ></v-text-field>
              </v-col
            ></v-row>
            <v-row
              ><v-col>
                <v-text-field
                  outlined
                  clearable
                  type="number"
                  v-model="settings.alertLevelGT"
                  dense
                  hide-details
                  label="Show Alert When >="
                ></v-text-field></v-col
              ><v-col>
                <v-text-field
                  outlined
                  clearable
                  type="number"
                  v-model="settings.alertLevelLT"
                  dense
                  hide-details
                  label="Show Alert When <="
                ></v-text-field></v-col
              ><v-col>
                <v-text-field
                  outlined
                  clearable
                  v-model="settings.alertLevelMessage"
                  dense
                  hide-details
                  label="Alert Tooltip"
                ></v-text-field
              ></v-col>
            </v-row>
            <v-row style="border: solid lightgray 1px;"><v-col>
            <v-row>
              <v-col cols="2" class="text-subtitle-2 mt-4">Number Format</v-col>
              <v-col cols="2">
                <v-text-field
                  outlined
                  clearable
                  type="number"
                  min="0"
                  max="10"
                  v-model="settings.numberFormat.decimalPlaces"
                  dense
                  hide-details
                  label="Decimal Places"
                ></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-switch
                  label="1000s Separator"
                  v-model="settings.numberFormat.thousandsSeparator"
                  dense
                  hide-details
                ></v-switch>
              </v-col>
              <v-col cols="2">
                <v-combobox
                  outlined
                  dense
                  hide-details
                  v-model="settings.numberFormat.prefix"
                  :items="utils.numberFormatOptions.commonPrefixes"
                  item-text="text"
                  item-value="value"
                  label="Prefix"
                  clearable
                ></v-combobox>
              </v-col>
              <v-col cols="2">
                <v-combobox
                  outlined
                  dense
                  hide-details
                  v-model="settings.numberFormat.suffix"
                  :items="utils.numberFormatOptions.commonSuffixes"
                  item-text="text"
                  item-value="value"
                  label="Suffix"
                  clearable
                ></v-combobox>
              </v-col></v-row>
            <v-row>
              <v-col cols="2"></v-col>
              <v-col cols="2">
                <v-select
                  outlined
                  dense
                  hide-details
                  v-model="settings.numberFormat.negativeFormat"
                  :items="utils.numberFormatOptions.negativeFormatOptions"
                  item-text="text"
                  item-value="value"
                  label="Negative Format"
                ></v-select>
              </v-col>
              <v-col cols="2">
                <v-switch
                  label="Show + sign"
                  v-model="settings.numberFormat.showPlusSign"
                  dense
                  hide-details
                  title="Show + sign for positive numbers"
                ></v-switch>
              </v-col>
              <v-col cols="2">
              </v-col>
              <v-col>
                <v-text-field style="display: inline-flex; width: 150px;"
                  outlined
                  dense
                  hide-details
                  type="number"
                  v-model="testValue"
                  label="Test Value"
                  placeholder="Enter a number to preview"
                ></v-text-field>
                <v-icon>mdi-arrow-right</v-icon>
                <div
                  class="format-preview"
                  :class="{
                    'red--text': testValue < 0 && ['redMinus', 'redBrackets'].includes(settings.numberFormat.negativeFormat)
                  }"
                >
                  {{ formattedTestValue }}
                </div>
              </v-col>
            </v-row>
            </v-col></v-row>
            <v-row>
              <v-col cols="3">Highlight Rules <v-icon @click="addhighlightRule">mdi-plus-box</v-icon>
              </v-col>
              <v-col cols="2">
                <v-switch
                  label="Show Key?"
                  v-model="settings.highlightRulesKeyVisible"
                ></v-switch> 
              </v-col>
              <v-col>
                <v-text-field v-if="settings.highlightRulesKeyVisible"
                  label="Key Addendum"
                  v-model="settings.highlightRulesKeyTextAddendum"
                ></v-text-field> 
              </v-col>
                <v-col>
                    <v-select dense outlined hide-details 
                    label="Min Sample measure:" 
                    clearable :items="measuresAvailable" item-value="id" item-text="title" 
                    v-model="settings.highLightMeasureMinimum"></v-select></v-col>
                <v-col cols="2">
                  <v-text-field type="number" label="Min Value"
                  outlined hide-details dense
                  v-model="settings.highLightMeasureMinimumValue"></v-text-field>
                </v-col>
            </v-row>
              <v-row dense v-for="(r, ri) in settings.highlightRules" :key="'hr' + ri">
                <v-col>
                  <v-text-field
                    outlined
                    clearable
                    type="number"
                    v-model="r.rangeFrom"
                    dense
                    hide-details
                    label="When >="
                  ></v-text-field></v-col>
                <v-col>
                  <v-text-field
                    outlined
                    clearable
                    type="number"
                    v-model="r.rangeTo"
                    dense
                    hide-details
                    label="When <="
                  ></v-text-field></v-col>
                <v-col>
                  <v-select
                    hide-details
                    outlined
                    dense
                    v-model="r.display_colour_id"
                    label="Colour"
                    :items="clientColours"
                    item-value="display_colour_id"
                    item-text="colour_name"
                  ></v-select
                ></v-col>
                <v-col cols="5">
                  <v-text-field
                    outlined
                    clearable
                    v-model="r.description"
                    dense
                    hide-details
                    label="Description"
                  ></v-text-field
                ></v-col>
                <v-col cols="1"><v-icon @click="removehighlightRule(ri)">mdi-minus-box</v-icon></v-col>
              </v-row>
            <v-row style="border: solid lightgray 1px;">
              <v-col>
                <v-switch
                  label="Allow Drill Down?"
                  v-model="settings.allowDrillDown"
                ></v-switch> 
                <v-row v-if="column.column_basis && settings.allowDrillDown">
              <template>
                <v-col>
                <v-text-field
                  outlined
                  clearable
                  v-model="settings.drillDownTitle"
                  dense
                  hide-details
                  label="Title"
                ></v-text-field
              ></v-col>
              <v-col>
                <v-select
                  hide-details
                  outlined
                  dense
                  v-model="settings.display_colour_id_primary"
                  label="Primary Value Colour"
                  :items="clientColours"
                  item-value="display_colour_id"
                  item-text="colour_name"
                ></v-select
              ></v-col>
              <v-col>
                <v-select
                  hide-details
                  outlined
                  dense
                  v-model="settings.display_colour_id_secondary"
                  label=""
                  :items="clientColours"
                  item-value="display_colour_id"
                  item-text="colour_name"
                ></v-select
              ></v-col>
              </template>
                </v-row>
            <v-row v-if="settings.allowDrillDown">
              <v-col cols="3" class="text-subtitle-2 mt-4">Drilldown Measure Format</v-col>
            <v-col cols="2">
                <v-text-field
                  outlined
                  clearable
                  type="number"
                  min="0"
                  max="10"
                  v-model="settings.drilldown.numberFormat.decimalPlaces"
                  dense
                  hide-details
                  label="Decimal Places"
                ></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-switch
                  label="1000s Separator"
                  v-model="settings.drilldown.numberFormat.thousandsSeparator"
                  dense
                  hide-details
                ></v-switch>
              </v-col>
              <v-col cols="2">
                <v-combobox
                  outlined
                  dense
                  hide-details
                  v-model="settings.drilldown.numberFormat.prefix"
                  :items="utils.numberFormatOptions.commonPrefixes"
                  item-text="text"
                  item-value="value"
                  label="Prefix"
                  clearable
                ></v-combobox>
              </v-col>
              <v-col cols="2">
                <v-combobox
                  outlined
                  dense
                  hide-details
                  v-model="settings.drilldown.numberFormat.suffix"
                  :items="utils.numberFormatOptions.commonSuffixes"
                  item-text="text"
                  item-value="value"
                  label="Suffix"
                  clearable
                ></v-combobox>
              </v-col>
              </v-row>
            <v-row v-if="settings.allowDrillDown">
              <v-col cols="3"></v-col>
              <v-col cols="3">
                <v-select
                  outlined
                  dense
                  hide-details
                  v-model="settings.drilldown.numberFormat.negativeFormat"
                  :items="utils.numberFormatOptions.negativeFormatOptions"
                  item-text="text"
                  item-value="value"
                  label="Negative Format"
                ></v-select>
              </v-col>
              <v-col>
                <v-switch
                  label="Show + sign for positive numbers"
                  v-model="settings.drilldown.numberFormat.showPlusSign"
                  dense
                  hide-details
                  title="Show + sign for positive numbers"
                ></v-switch>
              </v-col>
            </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="settings.show = false"> Cancel </v-btn>
          <v-btn text @click="saveSettings"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import utils from "@/common/utils.js";

export default {
  name: "HierarchyViewMeasurePicker",
  props: {
    column: Object,
    columnsAvailable: Array,
    columnsUsed: Array,
    allowRemove: Boolean,
    allowAdd: Boolean,
    measureDisplayTypes: Array,
    clientColours: Array,
    pickerOnly: Boolean,
    measuresAvailable: Array,
    allowDefaultSort: Boolean,
    defaultSort: String, // null | 'asc' | 'desc' — is this measure the view's default sort?
  },
  data: function () {
    return {
      utils: utils,
      settings: {
        show: false,
        displayType: null,
        columnTitle: null,
        hideSelector: false,
        alignment: "right",
        alertLevelGT: null,
        alertLevelLT: null,
        alertLevelMessage: null,
        highlightRules: [],
        allowDrillDown: false,
        drilldownMeasures: [],
        drillDownTitle: "",
        display_colour_id_primary: null,
        display_colour_id_secondary: null,
        highlightRulesKeyVisible: false,
        highlightRulesKeyTextAddendum: "",
        highLightMeasureMinimum: null,
        highLightMeasureMinimumValue: 0,
        single_comparator_text: null,
        defaultSort: null,
        // Number format settings
        numberFormat: {
          decimalPlaces: null,
          thousandsSeparator: true,
          prefix: "",
          suffix: "",
          negativeFormat: "minus", // 'minus', 'brackets', 'redMinus', 'redBrackets'
          showPlusSign: false, // Show + for positive numbers
        },
        drilldown: {
          numberFormat: {
            decimalPlaces: null,
            thousandsSeparator: true,
            prefix: "",
            suffix: "",
            negativeFormat: "minus", // 'minus', 'brackets', 'redMinus', 'redBrackets'
            showPlusSign: false, // Show + for positive numbers
          },
        },
      },
      testValue: -1234.5678,
      mode: "",
    };
  },
  components: {},
  watch: {},
  computed: {
    formattedTestValue() {
      return this.utils.formatNumber(this.testValue, this.settings.numberFormat);
    },
  },
  created: function () {},
  methods: {
    showColumnSettings() {
      const orig = JSON.parse(JSON.stringify(this.column));
      this.settings.displayType = orig.displayType;
      this.settings.columnTitle = orig.columnTitle;
      this.settings.hideSelector = orig.hideSelector || false;
      this.settings.allowDrillDown = orig.allowDrillDown || false;
      this.settings.drillDownTitle = orig.drillDownTitle || this.column.title;
      this.settings.alignment = orig.alignment;
      this.settings.alertLevelGT = orig.alertLevelGT;
      this.settings.alertLevelLT = orig.alertLevelLT;
      this.settings.alertLevelMessage = orig.alertLevelMessage;
      this.settings.highlightRules = (orig.highlightRules || []).filter(x => !x.noHighlight);
      this.settings.drilldownMeasures = orig.drilldownMeasures || [];
      this.settings.display_colour_id_primary = orig.display_colour_id_primary;
      this.settings.display_colour_id_secondary = orig.display_colour_id_secondary;
      this.settings.highlightRulesKeyVisible = orig.highlightRulesKeyVisible;
      this.settings.highlightRulesKeyTextAddendum = orig.highlightRulesKeyTextAddendum;
      this.settings.highLightMeasureMinimum = orig.highLightMeasureMinimum;
      this.settings.highLightMeasureMinimumValue = orig.highLightMeasureMinimumValue;
      this.settings.single_comparator_text = orig.single_comparator_text;
      this.settings.defaultSort = this.defaultSort || null;
      // Load number format settings
      this.settings.numberFormat = orig.numberFormat || {
        decimalPlaces: null,
        thousandsSeparator: true,
        prefix: "",
        suffix: "",
        negativeFormat: "minus",
        showPlusSign: false,
      };
      if (orig.drilldown?.numberFormat) {
        this.settings.drilldown.numberFormat = orig.drilldown.numberFormat;
      } else {
        this.settings.drilldown = {};
        this.settings.drilldown.numberFormat = {
              decimalPlaces: null,
              thousandsSeparator: true,
              prefix: "",
              suffix: "",
              negativeFormat: "minus",
              showPlusSign: false,
            };
      }
      this.settings.show = true;
    },
    saveSettings() {
      this.settings.show = false;
      this.settings.highlightRules = this.settings.highlightRules.filter(x => (x.rangeFrom !== null || x.rangeTo !== null) && x.display_colour_id && x.description);
      this.$emit(
        "changeColumnStyle",
        this.settings
      );
    },
    addhighlightRule() {
      this.settings.highlightRules.push({
        rangeFrom: null,
        rangeTo: null,
        display_colour_id: null,
        description: "",
        style: null,
      });
    },
    removehighlightRule(index) {
      this.settings.highlightRules.splice(index, 1);
    },
  },
};
</script>
  <style scoped lang="scss">
.columnAdder {
  background-color: #e0f2f1;
  width: 22px;
  display: inline-table;
  margin-left: 3px;
  padding-left: 0px;
  padding-right: 0px;

  border: solid 1px #bcd4d0;
  border-radius: 5px;
  height: 24px;
  font-size: 12px;
}
.columnPicker {
  background-color: #e0f2f1;
  /*   width: calc(100% - 52px);
   display: inline-table; 
  display: flex;
  width: 100%;*/
  border: solid 1px #bcd4d0;
  border-radius: 5px;
  /* height: 24px; */
  font-size: 12px;
  padding-left: 6px;
  padding-right: 6px;
  font-weight: bold;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
.pickerContent {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  display: inline-flex;
  padding-top: 3px;
  width: 100%;
}
.format-preview {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 12px;
  font-family: monospace;
  font-size: 14px;
  min-height: 38px;
  display: flex;
  align-items: center;
  display: inline-flex;
}
.picker-only {
  border: 1px solid lightgray;
  border-radius: 8px;
  background-color: white;
  padding-left: 16px;
  padding-top: 8px;
}
.picker-only-text {

  font-family: 'DM Sans';
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  letter-spacing: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: calc(100% - 30px);
  display: inline-block;
}
</style>